<template>
    <div class="blog">
        <section class="blog-home">
            <div class="container">
                <h1 class="blog-title">Blog</h1>
            </div>
        </section>
        <div class="container section_holder">
            <div class="section_title">
                <SidebarBlog
                    :searchTerms="searchTerms"
                    :presetTags="presetTags"
                    @addSearch="addSearch"
                    @removeSearch="removeSearch"
                    @searchByTag="searchByTag"
                    @searchByTags="searchByTags"/>
            </div>
            <div class="section_content">
                <!-- Semantic search results (replaces the blog list while searching) -->
                <template v-if="searching">
                    <h2 class="semantic-results__title">Search Results</h2>
                    <div v-if="semantic && semantic.loading && !semantic.results.length" class="semantic-results__meta">
                        Searching…
                    </div>
                    <template v-else-if="semantic && semantic.error">
                        <div class="semantic-results__meta">
                            Semantic search is offline — showing simple keyword matches.
                        </div>
                        <BlogContent :blog="fallbackItems" :tagFromPost='[]'/>
                    </template>
                    <template v-else-if="semantic">
                        <div class="semantic-results__meta">
                            Semantic search · {{ filteredResults.length }} result{{ filteredResults.length === 1 ? '' : 's' }}
                            <span v-if="semantic.tookMs != null"> · {{ semantic.tookMs }} ms</span>
                            · <b>tap a match badge to see why</b>
                        </div>
                        <div v-for="r in filteredResults" :key="r.url" class="blog-item semantic-item">
                            <div class="blog-item__image" v-if="imgFor(r)">
                                <router-link :to="toPath(r.url)">
                                    <img :src="imgFor(r)" :alt="r.title" loading="lazy"/>
                                </router-link>
                                <span class="semantic-chip" :class="'semantic-chip--' + r.matchLevel"
                                    @click.stop="openWhy = openWhy === r.url ? null : r.url">
                                    {{ chipLabel(r.matchLevel) }} <span class="semantic-chip__i">ⓘ</span>
                                </span>
                                <div v-if="openWhy === r.url" class="semantic-why" @click.stop>
                                    <div class="semantic-why__head">
                                        <b>Why this result?</b>
                                        <span class="semantic-chip" :class="'semantic-chip--' + r.matchLevel">{{ chipLabel(r.matchLevel) }}</span>
                                    </div>
                                    <p class="semantic-why__verdict">{{ whyVerdict(r) }}</p>
                                    <div class="semantic-why__row">
                                        <span>Meaning (semantic)</span>
                                        <span class="semantic-why__bar"><span :style="{width: meaningPct(r) + '%'}"></span></span>
                                        <span>{{ (r.vectorScore || 0).toFixed(2) }}</span>
                                    </div>
                                    <div class="semantic-why__row">
                                        <span>Words (keyword)</span>
                                        <span class="semantic-why__bar semantic-why__bar--kw"><span :style="{width: wordsPct(r) + '%'}"></span></span>
                                        <span>{{ wordsPct(r) }}%</span>
                                    </div>
                                    <div class="semantic-why__total">
                                        Combined score <b>{{ (r.score || 0).toFixed(3) }}</b>
                                        = meaning {{ contribution(r).vc.toFixed(2) }} ({{ Math.round(weightsFor(r).vector * 100) }}%)
                                        + words {{ contribution(r).kc.toFixed(2) }} ({{ Math.round(weightsFor(r).keyword * 100) }}%)
                                    </div>
                                </div>
                            </div>
                            <div class="semantic-item__meta">
                                {{ [r.category, r.author, r.date].filter(x => x && x !== '—').join(' · ') }}
                            </div>
                            <div class="blog-item__title">
                                <router-link :to="toPath(r.url)">{{ r.title }}</router-link>
                            </div>
                            <div class="blog-item__content">
                                <p>{{ r.excerpt }}…</p>
                            </div>
                            <div class="btn-read-more primary contacts-button small">
                                <router-link :to="toPath(r.url)" class="button-content">
                                    Read more
                                    <span class="icon">
                                        <img v-svg-inline :data-src="require('@/assets/icons/rarr-icon.svg')" alt="" />
                                    </span>
                                </router-link>
                            </div>
                        </div>
                        <p v-if="!filteredResults.length && !semantic.loading" class="semantic-results__meta">
                            Nothing matches this query{{ activeTagsLocal.length ? ' and the selected tags' : '' }}.
                            Try rephrasing — the search reads meaning, not just keywords.
                        </p>
                    </template>
                </template>

                <!-- Normal blog list -->
                <template v-else>
                    <BlogContent :blog="getItems" :tagFromPost='[]'/>
                    <div class="section_pagination">
                        <paginate
                            v-if="getPaginateCount > 1"
                            :page-count="getPaginateCount"
                            :page-range="5"
                            :margin-pages="1"
                            :initial-page="currentPage"
                            :click-handler="paginateClickCallback"
                            :prev-text="''"
                            :next-text="''"
                            :container-class="'pagination'"
                            :page-class="'page-item'"
                            class="blog_paginate"
                        >
                        </paginate>
                    </div>
                </template>
            </div>
        </div>
        <!-- click-away for the "why" popup -->
        <div v-if="openWhy" class="semantic-why__backdrop" @click="openWhy = null"></div>
    </div>
</template>

<script>
import SidebarBlog from '../components/Blog/Sidebar.vue';
import BlogContent from '../components/Blog/BlogContent.vue';
import {mapState} from 'vuex'
import Paginate from 'vuejs-paginate-next';
/* Map of site paths → each page's own image (auto-generated) so search result
   cards show the real page banner instead of a random blog image. */
import searchImages from '../search-images.js';

export default {
    components: {
        SidebarBlog,
        BlogContent,
        Paginate
    },
    data() {
        return {
            currentPage: 1,
            perPage: 5,
            content: '',
            /* Semantic search state. The sidebar owns only the input UI; the
               terms, the API call and the results all live here. */
            searchTerms: [],
            semantic: null,        // { loading, results, error, tookMs, weights }
            presetTags: null,      // pushed to the sidebar after each search (auto-select)
            activeTagsLocal: [],   // current tag selection (mirrors the sidebar)
            openWhy: null,         // url of the result whose "why" popup is open
        }
    },
    computed: {
        ...mapState(['blog', 'hitech_projects', 'mob_projects', 'other_projects', 'magento_projects']),
        /* Portfolio project pages: /portfolio/project/<id> → the project's own
           head image (same asset the project page renders). */
        portfolioImgBySlug() {
            const m = {};
            for (const list of [this.hitech_projects, this.mob_projects, this.other_projects, this.magento_projects]) {
                for (const item of (list || [])) {
                    const head = item.product && item.product.head_image;
                    if (item.id && head) m[item.id] = head;
                }
            }
            return m;
        },
        getItems: function () {
            let start = (this.currentPage - 1) * this.perPage;
            let end = this.currentPage * this.perPage;
            return this.content.slice(start, end);
        },
        getPaginateCount: function () {
            return Math.ceil(this.content.length / this.perPage);
        },
        searching() {
            return this.searchTerms.length > 0;
        },
        /* Blog posts by their URL slug so semantic results can pick up the
           local preview image and tags. */
        blogBySlug() {
            const m = {};
            for (const p of this.blog) m[p.id] = p;
            return m;
        },
        /* Results shown = API results filtered by the currently selected tags
           (auto-selected after the search; the user removes tags to exclude
           posts). Site pages carry no tags, so only blog posts are filterable. */
        filteredResults() {
            if (!this.semantic || this.semantic.error) return [];
            const res = this.semantic.results || [];
            if (!this.activeTagsLocal.length) return res;
            return res.filter(r => {
                const p = this.postFor(r);
                if (!p) return true; // site page / not in the local catalog
                return p.tags.some(t => this.activeTagsLocal.includes(t));
            });
        },
        /* Plain substring fallback when the semantic API is unreachable. */
        fallbackItems() {
            const qs = this.searchTerms.map(s => s.toLowerCase());
            return this.blog.filter(item => qs.some(q =>
                item.title.toLowerCase().includes(q) ||
                item.desc.toLowerCase().includes(q) ||
                item.tags.some(t => t.toLowerCase().includes(q))
            ));
        },
    },
    methods: {
        paginateClickCallback: function (pageNum) {
            document.documentElement.scrollTop = 200
            this.currentPage = Number(pageNum);
            sessionStorage.currentPage = this.currentPage
        },
        searchByTag(tag) {
            if (tag.length) {
                this.content = this.blog.filter(item => item.tags.find(t => t === tag))
            } else {
                this.content = this.blog
            }
        },
        searchByTags(tags) {
            this.activeTagsLocal = tags.slice();
            if (tags.length) {
                this.content = this.blog.filter(item => item.tags.some(i => tags.includes(i)))
            } else {
                this.content = this.blog
            }
        },
        addSearch(q) {
            const s = q.trim();
            if (!s) return;
            if (this.searchTerms.some(x => x.toLowerCase() === s.toLowerCase())) return;
            this.searchTerms = [...this.searchTerms, s];
        },
        removeSearch(q) {
            this.searchTerms = this.searchTerms.filter(x => x !== q);
        },
        runSemantic() {
            const terms = this.searchTerms;
            if (!terms.length) return;
            this.semantic = { loading: true, results: (this.semantic && this.semantic.results) || [], error: null };
            fetch('/api/wdevs/search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: terms.join(' '), topK: 12 }),
            })
                .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
                .then(data => {
                    if (this.searchTerms !== terms) return; // stale response
                    if (data.error) {
                        this.semantic = { loading: false, results: [], error: data.error };
                        return;
                    }
                    this.semantic = {
                        loading: false,
                        results: data.results || [],
                        error: null,
                        tookMs: data.tookMs,
                        weights: data.weights,
                    };
                    /* Auto-select the tags of the found posts: they light up in
                       the sidebar and the user narrows the output by removing
                       them — same flow as the Action Library prototype. */
                    const tags = new Set();
                    for (const r of this.semantic.results) {
                        const p = this.postFor(r);
                        if (p) p.tags.forEach(t => tags.add(t));
                    }
                    this.presetTags = [...tags];
                })
                .catch(e => {
                    if (this.searchTerms !== terms) return;
                    this.semantic = { loading: false, results: [], error: String(e && e.message || e) };
                });
        },
        slugOf(url) {
            const path = this.toPath(url);
            return path.split('/').filter(Boolean).pop() || '';
        },
        toPath(url) {
            return String(url || '').replace(/^https?:\/\/(www\.)?wdevs\.com/, '') || '/';
        },
        postFor(r) {
            return this.blogBySlug[this.slugOf(r.url)] || null;
        },
        imgFor(r) {
            // 1. Blog posts — their own preview image from the catalog
            const p = this.postFor(r);
            if (p && p.img_src) {
                try { return require('../assets' + p.img_src + '.jpg'); } catch (e) { /* fall through */ }
            }
            const sitePath = this.toPath(r.url);
            // 2. Site pages — the page's own banner from the generated map
            if (searchImages[sitePath]) return searchImages[sitePath];
            // 3. Portfolio projects — the project's head image from the store
            const mProj = sitePath.match(/^\/portfolio\/project\/(.+)$/);
            if (mProj && this.portfolioImgBySlug[mProj[1]]) {
                const head = this.portfolioImgBySlug[mProj[1]];
                try { return require(`../assets/portfolio/spa-portfolio/head_product_images/${head}.jpg`); }
                catch (e) {
                    try { return require(`../assets/portfolio/spa-portfolio/head_product_images/${head}.png`); }
                    catch (e2) { /* fall through */ }
                }
            }
            // 4. Fallback — a stable pick from the blog image pool
            const pool = this.blog.map(b => b.img_src);
            if (!pool.length) return null;
            let h = 0;
            const s = r.title || '';
            for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
            try {
                return require('../assets' + pool[h % pool.length] + '.jpg');
            } catch (e) {
                return null;
            }
        },
        chipLabel(level) {
            return level === 'high' ? 'High match' : level === 'medium' ? 'Medium match' : 'Low match';
        },
        weightsFor(r) {
            return r.weights || (this.semantic && this.semantic.weights) || { vector: 0.7, keyword: 0.3 };
        },
        contribution(r) {
            const w = this.weightsFor(r);
            return { vc: w.vector * (r.vectorScore || 0), kc: w.keyword * (r.keywordScore || 0) };
        },
        meaningPct(r) {
            return Math.min(100, Math.round(((r.vectorScore || 0) / 0.7) * 100));
        },
        wordsPct(r) {
            return Math.round((r.keywordScore || 0) * 100);
        },
        whyVerdict(r) {
            const { vc, kc } = this.contribution(r);
            if (kc >= vc * 1.3) return 'Surfaced mainly because your search words appear in the title or text.';
            if (vc >= kc * 1.3) return 'Surfaced mainly by meaning — it covers a closely related topic even without your exact words.';
            return 'Surfaced by a mix of meaning and your exact words.';
        },
    },
    watch: {
        content(newVal) {
            this.content = newVal
        },
        searchTerms(newVal) {
            this.openWhy = null;
            if (!newVal.length) {
                /* Search cleared — drop the results and the auto-selected tags. */
                this.semantic = null;
                this.presetTags = [];
                return;
            }
            this.runSemantic();
        },
    },
    created() {
        this.content = this.blog
        if (sessionStorage.currentPage && history.state.forward !== null) {
            this.currentPage = +sessionStorage.currentPage
        } else {
            sessionStorage.currentPage = 1
            this.currentPage = 1
        }

    }
}
</script>

<style lang="scss">
@import "../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";

.blog {
    .section {
        &_holder {
            display: flex;
            flex-direction: column-reverse;
            @include media(">=tablet-xl") {
                flex-direction: row;
            }
        }
        &_title {
            width: 100%;
            margin-right: 60px;
            margin-bottom: 0;
            padding-bottom: 0;
            @include media(">=tablet-xl") {
                width: 35%;
                margin-right: 43px;
            }
        }
        &_content {
            display: block;
            width: 100%;
            margin-bottom: 60px;
            margin-left: 0;
            @include media(">=tablet-xl") {
                width: 65%;
                margin-bottom: 0;
                margin-left: 37px;
            }
        }
        &_pagination {
            padding-top: 40px;
        }
    }

    &-home {
        padding: 60px 0;
        @include media(">=desktop") {
            padding: 80px 0;
        }
    }

    &-title {
        margin: 0;
        font-size: 34px;
        line-height: 111%;
        text-transform: uppercase;
        color: $black;
        text-align: center;
        @include media(">=tablet") {
            font-size: 50px;
        }
        @include media(">=tablet-xl") {
            font-size: 60px;
        }
        @include media(">=desktop") {
            font-size: 64px;
        }
    }
}

.row {
    display: flex;
    .col:first-child {
        margin-right: 80px;
    }
}

.blog_paginate {
    display: flex;
    justify-content: center;
    padding-left: 0;
    list-style-type: none;
    color: $red;
    @include media(">=tablet-xl") {
        justify-content: flex-start;
    }
    .page {
        &-item {
            &:first-child,
            &:last-child {
                display: none;
            }
            &.active {
                .page-link {
                    color: $white;
                    background-color: $red;
                }
            }
        }
        &-link {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 36px;
            height: 36px;
            margin-right: 10px;
            border: 2px solid $red;
            border-radius: 50%;
            padding: 5px 10px;
            font-weight: 700;
            font-size: 16px;
            line-height: 28px;
            color: $red;
            cursor: pointer;
            transition: 0.25s color ease-in-out, 0.25s background-color ease-in-out;
            @include media(">=tablet-xl") {
                width: 50px;
                height: 50px;
                margin-right: 20px;
                padding: 14px 20px;
            }
            &:hover {
                color: $white;
                background-color: $red;
            }
        }
    }
}

/* ── Semantic search results ─────────────────────────────────────────────── */
.semantic-results__title {
    margin: 0 0 10px;
    font-size: 30px;
    line-height: 36px;
    font-weight: 700;
    color: $black;
    @include media(">=desktop") {
        font-size: 40px;
        line-height: 48px;
    }
}
.semantic-results__meta {
    margin-bottom: 24px;
    font-size: 16px;
    line-height: 24px;
    color: $grey;
    b { color: $black; font-weight: 600; }
}
.semantic-item {
    .blog-item__image {
        position: relative;
    }
    .semantic-item__meta {
        padding-top: 14px;
        font-size: 14px;
        line-height: 20px;
        color: $grey;
    }
    .blog-item__title {
        padding-top: 8px;
    }
}
.semantic-chip {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 16px;
    font-weight: 700;
    cursor: pointer;
    user-select: none;
    box-shadow: 0 1px 4px $boxShadow;
    &--high   { background: #e7f6ec; color: #1a7f37; }
    &--medium { background: #fff6d6; color: #9a6700; }
    &--low    { background: #f2f4f6; color: #6a737d; }
    &__i { opacity: .75; font-size: 12px; }
}
.semantic-why {
    position: absolute;
    top: 44px;
    right: 12px;
    z-index: 60;
    width: unquote("min(360px, 92%)");
    padding: 16px 18px;
    background: $white;
    border: 1px solid $borderInput;
    border-radius: 10px;
    box-shadow: 0 14px 40px -12px rgba(0,0,0,.35);
    cursor: default;
    &__backdrop {
        position: fixed;
        inset: 0;
        z-index: 50;
    }
    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
        b { font-size: 17px; color: $black; }
        .semantic-chip { position: static; cursor: default; box-shadow: none; }
    }
    &__verdict {
        margin: 0 0 12px;
        font-size: 14px;
        line-height: 20px;
        color: $grey;
    }
    &__row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
        font-size: 13px;
        color: $black;
        > span:first-child { width: 128px; flex-shrink: 0; color: $grey; }
        > span:last-child { width: 40px; flex-shrink: 0; text-align: right; }
    }
    &__bar {
        flex: 1;
        height: 6px;
        border-radius: 3px;
        background: $gray;
        overflow: hidden;
        span {
            display: block;
            height: 100%;
            border-radius: 3px;
            background: $blue;
        }
        &--kw span { background: $red; }
    }
    &__total {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid $gray;
        font-size: 12px;
        line-height: 18px;
        color: $grey;
        b { color: $black; }
    }
}
</style>
